import { Component, HostBinding, OnChanges, SimpleChanges, inject, Input, ViewChild } from '@angular/core';
import { BaseModelKeys } from 'ag-common-lib/lib/models/base.model';
import {
  AgentKeys,
  Conference,
  ConferenceKeys,
  ConferenceLogBulkInviteeOutcomeStatusKeys,
  Constants,
  Entity,
  EntityPermissionActivityKeys, INVITEE_OUTCOME_STATUS_LOOKUP, INVITEE_STATUS_LOOKUP, LookupKeys,
} from '@ag-common-lib/public-api';
import { DxDataGridComponent } from 'devextreme-angular';
import CustomStore from 'devextreme/data/custom_store';
import { BehaviorSubject, firstValueFrom, startWith, take } from 'rxjs';
import { ConferenceBulkInviteeOutcomeStatusService } from 'ag-common-svc/lib/services/conference-bulk-invitee-outcome-status.service';
import { AgentService } from 'ag-common-svc/lib/services/agent.service';
import { AgentsMapService } from 'ag-common-svc/public-api';
import { ConferenceLogBulkInviteeOutcomeStatus } from 'ag-common-lib/public-api';

@Component({
  selector: 'ag-crm-conference-bulk-outcome-status-updates',
  templateUrl: './conference-bulk-outcome-status-updates.component.html',
  styleUrls: ['./conference-bulk-outcome-status-updates.component.scss'],
  providers: [ConferenceBulkInviteeOutcomeStatusService],
})
export class ConferenceBulkOutcomeStatusUpdatesComponent implements OnChanges {
  @HostBinding('class') className = 'conference-bulk-outcome-status-updates';
  @ViewChild('logOutcomeStatusesGridRef') logGrid: DxDataGridComponent;

  @Input() conference: Partial<Conference>;

  private logsManagerService = inject(ConferenceBulkInviteeOutcomeStatusService);
  private agentService = inject(AgentService);
  private agentsMapService = inject(AgentsMapService);
  /**
   * The `createdByAgentDbId` of whatever the grid last loaded — what the cell template
   * resolves names from. Published on every load path, cached ones included, so the map
   * follows the grid rather than holding the previous conference's authors.
   */
  private readonly renderedAgentDbIds$ = new BehaviorSubject<string[]>([]);
  private logsCache = new Map<string, ConferenceLogBulkInviteeOutcomeStatus[]>();
  private logsPendingRequests = new Map<string, Promise<ConferenceLogBulkInviteeOutcomeStatus[]>>();

  protected readonly BaseModelKeys = BaseModelKeys;
  protected readonly AgentKeys = AgentKeys;
  protected readonly ConferenceKeys = ConferenceKeys;
  protected readonly ConferenceLogBulkInviteeOutcomeStatusKeys = ConferenceLogBulkInviteeOutcomeStatusKeys;
  protected readonly Entity = Entity;
  protected readonly EntityPermissionActivityKeys = EntityPermissionActivityKeys;
  protected readonly inviteeOutcomeStatusLookup = INVITEE_OUTCOME_STATUS_LOOKUP;
  protected readonly LookupKeys = LookupKeys;
  readonly dateFormat: string = Constants.MMDDYYYY_DATE_SHORT_TIME_FORMAT;

  outcomeStatusUpdatesDataSource = new CustomStore({
    key: BaseModelKeys.dbId,
    load: () => {
      const conferenceDbId = this.conference?.[BaseModelKeys.dbId];
      if (!conferenceDbId) {
        return Promise.resolve({ data: [], totalCount: 0 });
      }

      const cachedLogs = this.logsCache.get(conferenceDbId);
      if (cachedLogs) {
        return Promise.resolve(this.toLoadResult(cachedLogs));
      }

      const pendingRequest = this.logsPendingRequests.get(conferenceDbId);
      if (pendingRequest) {
        return pendingRequest.then(logs => this.toLoadResult(logs));
      }

      const loadPromise = firstValueFrom(
        this.logsManagerService.getConferenceOutcomeStatusLogsByConferenceId(conferenceDbId).pipe(take(1)),
      )
        .then(logs => {
          const safeLogs = logs ?? [];
          this.logsCache.set(conferenceDbId, safeLogs);
          this.logsPendingRequests.delete(conferenceDbId);
          return safeLogs;
        })
        .catch(error => {
          this.logsPendingRequests.delete(conferenceDbId);
          console.error('Failed to load bulk outcome status updates', error);
          return [];
        });

      this.logsPendingRequests.set(conferenceDbId, loadPromise);

      return loadPromise.then(logs => this.toLoadResult(logs));
    },
  });
  protected readonly agentsMap$ = this.agentsMapService.byDbIds(this.renderedAgentDbIds$).pipe(startWith({}));
  protected readonly agentsLookupDataSourceConfiguration = this.agentService.agentsDataSourceConfiguration;

  private toLoadResult(logs: ConferenceLogBulkInviteeOutcomeStatus[]) {
    const safeLogs = logs ?? [];

    this.renderedAgentDbIds$.next(safeLogs.map(log => log?.[BaseModelKeys.createdByAgentDbId]));

    return { data: safeLogs, totalCount: safeLogs.length };
  }

  ngOnChanges(changes: SimpleChanges): void {
    const currentConferenceId = changes['conference']?.currentValue?.[BaseModelKeys.dbId];
    const previousConferenceId = changes['conference']?.previousValue?.[BaseModelKeys.dbId];

    if (currentConferenceId !== previousConferenceId) {
      this.logsCache.clear();
      this.logsPendingRequests.clear();
    }
  }
}
